import type { ChatModelAdapter } from "@assistant-ui/react";
import { SocketChatConnection, type ResetSessionOptions } from "./socketConnection";
import {
  createClientMessageId,
  createEmptySessionIdentity,
  extractLastUserClientMessageId,
  extractLastUserText,
  type SocketSessionIdentity,
} from "./sessionIdentity";
import { socketStatusActions } from "./socketStatusStore";
import type {
  FollowUpSuggestion,
  GuestSessionBootstrapOptions,
  QualityProfile,
  SessionResumePayload,
  SocketFactory,
  SupervisorHandoffPayload,
  SupervisorLiveEndedPayload,
  SupervisorLiveErrorPayload,
  SupervisorLiveMessagePayload,
  SupervisorResponseChunkPayload,
  SupervisorResponseErrorPayload,
  SupervisorResponseFinalPayload,
  SupervisorRoutingStartedPayload,
  SupervisorStageUpdatedPayload,
  SessionTurnPersistedPayload,
  WidgetConversationSyncPayload,
  WidgetFeedbackSubmitPayload,
  WidgetMessageSyncPayload,
  WidgetPresenceSyncPayload,
  WidgetShellSyncPayload,
  WidgetUnreadSyncPayload,
} from "./types";
import type { SocketInitialSessionIdentity } from "./sessionIdentity";

export type SocketChatRuntimeController = {
  adapter: ChatModelAdapter;
  connect: () => Promise<void>;
  requestSessionResume: () => void;
  getIdentity: () => SocketSessionIdentity;
  publishWidgetShellSync: (payload: WidgetShellSyncPayload) => Promise<boolean>;
  publishWidgetConversationSync: (payload: WidgetConversationSyncPayload) => Promise<void>;
  publishWidgetMessageSync: (payload: WidgetMessageSyncPayload) => Promise<void>;
  publishWidgetPresenceSync: (payload: WidgetPresenceSyncPayload) => Promise<void>;
  publishWidgetUnreadSync: (payload: WidgetUnreadSyncPayload) => Promise<void>;
  publishWidgetFeedbackSubmit: (payload: WidgetFeedbackSubmitPayload) => Promise<void>;
  endLiveSession: (reason?: string) => Promise<void>;
  startNewChat: (conversationSync?: WidgetConversationSyncPayload) => Promise<SessionResumePayload | null>;
  disconnect: () => void;
  resetSession: (options?: ResetSessionOptions) => void;
};

export type MessageSentPayload = {
  clientMessageId: string;
  threadId: string | null;
  /**
   * The user message text. Always populated by the adapter.
   * Consumers are responsible for honoring `includeMessageBody` before
   * including this in analytics track events.
   */
  message: string;
};

export type MessageReceivedPayload = {
  traceId?: string;
  threadId: string | null;
  agentId?: string | null;
  agentName?: string | null;
  /**
   * The assistant response text from the final payload. Always populated by
   * the adapter when present in supervisor.response.final.
   * Consumers are responsible for honoring `includeMessageBody` before
   * including this in analytics track events.
   */
  response?: string;
};

export type SocketChatRuntimeControllerOptions = {
  backendUrl?: string;
  initialIdentity?: SocketInitialSessionIdentity;
  qualityProfile?: QualityProfile;
  modelId?: string;
  socketFactory?: SocketFactory;
  guestBootstrap?: GuestSessionBootstrapOptions | false;
  resetGuestOnNextBootstrap?: boolean;
  createClientMessageId?: () => string;
  shouldAcceptSessionResume?: (payload: SessionResumePayload) => boolean;
  onSessionResume?: (payload: SessionResumePayload) => void;
  onSessionIdentityUpdated?: (identity: SocketSessionIdentity) => void;
  onHandoffStarted?: (payload: SupervisorHandoffPayload) => void;
  onHandoffCompleted?: (payload: SupervisorHandoffPayload) => void;
  onHandoffFailed?: (payload: SupervisorHandoffPayload) => void;
  onLiveMessage?: (payload: SupervisorLiveMessagePayload) => void;
  onLiveSystemMessage?: (payload: SupervisorLiveMessagePayload) => void;
  onLiveEnded?: (payload: SupervisorLiveEndedPayload) => void;
  onLiveError?: (payload: SupervisorLiveErrorPayload) => void;
  onSupervisorRoutingStarted?: (payload: SupervisorRoutingStartedPayload) => void;
  onSupervisorStageUpdated?: (payload: SupervisorStageUpdatedPayload) => void;
  onSupervisorResponseChunk?: (payload: SupervisorResponseChunkPayload) => void;
  onSupervisorResponseFinal?: (payload: SupervisorResponseFinalPayload) => void;
  onSupervisorResponseError?: (payload: SupervisorResponseErrorPayload) => void;
  onMessageSent?: (payload: MessageSentPayload) => void;
  onMessageReceived?: (payload: MessageReceivedPayload) => void;
  onSuggestionsReceived?: (clientMessageId: string, suggestions: FollowUpSuggestion[]) => void;
  onTurnPersisted?: (payload: SessionTurnPersistedPayload) => void;
  onWidgetShellSync?: (payload: WidgetShellSyncPayload) => void;
  onWidgetConversationSync?: (payload: WidgetConversationSyncPayload) => void;
  onWidgetMessageSync?: (payload: WidgetMessageSyncPayload) => void;
  onWidgetPresenceSync?: (payload: WidgetPresenceSyncPayload) => void;
  onWidgetUnreadSync?: (payload: WidgetUnreadSyncPayload) => void;
};

export function createSocketChatRuntimeController({
  backendUrl,
  initialIdentity,
  qualityProfile,
  modelId,
  socketFactory,
  guestBootstrap,
  resetGuestOnNextBootstrap,
  createClientMessageId: createMessageId = createClientMessageId,
  shouldAcceptSessionResume,
  onSessionResume,
  onSessionIdentityUpdated,
  onHandoffStarted,
  onHandoffCompleted,
  onHandoffFailed,
  onLiveMessage,
  onLiveSystemMessage,
  onLiveEnded,
  onLiveError,
  onSupervisorRoutingStarted,
  onSupervisorStageUpdated,
  onSupervisorResponseChunk,
  onSupervisorResponseFinal,
  onSupervisorResponseError,
  onMessageSent,
  onMessageReceived,
  onSuggestionsReceived,
  onTurnPersisted,
  onWidgetShellSync,
  onWidgetConversationSync,
  onWidgetMessageSync,
  onWidgetPresenceSync,
  onWidgetUnreadSync,
}: SocketChatRuntimeControllerOptions): SocketChatRuntimeController {
  if (!backendUrl) {
    const adapter: ChatModelAdapter = {
      async *run() {
        throw new Error("Socket.IO backend URL is required for Phoebe chat.");
      },
    };
    return {
      adapter,
      connect: async () => undefined,
      requestSessionResume: () => undefined,
      getIdentity: () => createEmptySessionIdentity(),
      publishWidgetShellSync: async () => false,
      publishWidgetConversationSync: async () => undefined,
      publishWidgetMessageSync: async () => undefined,
      publishWidgetPresenceSync: async () => undefined,
      publishWidgetUnreadSync: async () => undefined,
      publishWidgetFeedbackSubmit: async () => undefined,
      endLiveSession: async () => undefined,
      startNewChat: async () => null,
      disconnect: () => socketStatusActions.reset(),
      resetSession: () => socketStatusActions.reset(),
    };
  }

  const connection = new SocketChatConnection({
    backendUrl,
    initialIdentity,
    socketFactory,
    guestBootstrap,
    resetGuestOnNextBootstrap,
    shouldAcceptSessionResume,
    onSessionIdentityUpdated,
    onSessionResume: (payload) => {
      socketStatusActions.applySessionResume(payload);
      onSessionResume?.(payload);
    },
    onHandoffStarted: (payload) => {
      socketStatusActions.markHandoffStarted(payload);
      onHandoffStarted?.(payload);
    },
    onHandoffCompleted: (payload) => {
      if (!socketStatusActions.markHandoffCompleted(payload)) return;
      onHandoffCompleted?.(payload);
    },
    onHandoffFailed: (payload) => {
      if (!socketStatusActions.markHandoffFailed(payload)) return;
      onHandoffFailed?.(payload);
    },
    onLiveMessage: (payload) => {
      if (!socketStatusActions.appendLiveMessage(payload)) return;
      onLiveMessage?.(payload);
    },
    onLiveSystemMessage: (payload) => {
      if (!socketStatusActions.appendLiveMessage(payload)) return;
      onLiveSystemMessage?.(payload);
    },
    onLiveTyping: socketStatusActions.updateLiveTyping,
    onLiveEnded: (payload) => {
      if (!socketStatusActions.markLiveEnded(payload)) return;
      onLiveEnded?.(payload);
    },
    onLiveError: (payload) => {
      if (!socketStatusActions.failLiveAssistance(payload)) return;
      onLiveError?.(payload);
    },
    onLiveReceipt: socketStatusActions.applyLiveReceipt,
    onLiveMessageForwarded: socketStatusActions.markMessageForwarded,
    onSupervisorRoutingStarted,
    onSupervisorStageUpdated,
    onSupervisorResponseChunk,
    onSupervisorResponseFinal,
    onSupervisorResponseError,
    onSuggestionsReceived,
    onTurnPersisted,
    onWidgetShellSync,
    onWidgetConversationSync,
    onWidgetMessageSync,
    onWidgetPresenceSync,
    onWidgetUnreadSync,
  });

  const adapter: ChatModelAdapter = {
    async *run({ messages, abortSignal }) {
      const message = extractLastUserText(messages);
      if (!message.trim()) return;

      const clientMessageId = extractLastUserClientMessageId(messages) ?? createMessageId();
      socketStatusActions.startTurn(clientMessageId);

      try {
        for await (const update of connection.runTurn({
          message,
          clientMessageId,
          abortSignal,
          qualityProfile,
          modelId,
          onAck: (payload) => {
            socketStatusActions.acknowledgeTurn(payload);
            if (payload.status === "live_forwarded") {
              socketStatusActions.appendLiveStudentMessage({
                live_session_id: payload.live_session_id,
                client_message_id: clientMessageId,
              });
            }
            // Fire Message Sent on the first server ack — this confirms the
            // backend received the message, avoiding a phantom event if
            // connection.runTurn() rejects before the socket emits.
            onMessageSent?.({
              clientMessageId,
              threadId: payload.thread_id ?? connection.getIdentity().threadId,
              message,
            });
          },
          onRoutingStarted: socketStatusActions.markRoutingStarted,
          onStageUpdated: socketStatusActions.updateStage,
          onError: socketStatusActions.failTurn,
          onFinal: (payload) => {
            // Fire Message Received on final payload — agent metadata is
            // available here but not on individual chunks.
            const agentMetadata = payload.agent_metadata ?? null;
            onMessageReceived?.({
              traceId: payload.trace_id,
              threadId: payload.thread_id ?? connection.getIdentity().threadId,
              agentId: agentMetadata?.routed_to?.domain ?? null,
              agentName: agentMetadata?.routed_to?.agent_name ?? null,
              response: payload.response?.text ?? payload.text,
            });
          },
        })) {
          yield update;
        }
      } finally {
        // finishTurn lives only here — handles normal completion, abort, and error.
        socketStatusActions.finishTurn(clientMessageId);
      }
    },
  };

  return {
    adapter,
    connect: () => connection.connect(),
    requestSessionResume: () => connection.requestSessionResume(),
    getIdentity: () => connection.getIdentity(),
    publishWidgetShellSync: (payload: WidgetShellSyncPayload) => connection.publishWidgetShellSync(payload),
    publishWidgetConversationSync: (payload: WidgetConversationSyncPayload) =>
      connection.publishWidgetConversationSync(payload),
    publishWidgetMessageSync: (payload: WidgetMessageSyncPayload) => connection.publishWidgetMessageSync(payload),
    publishWidgetPresenceSync: (payload: WidgetPresenceSyncPayload) => connection.publishWidgetPresenceSync(payload),
    publishWidgetUnreadSync: (payload: WidgetUnreadSyncPayload) => connection.publishWidgetUnreadSync(payload),
    publishWidgetFeedbackSubmit: (payload: WidgetFeedbackSubmitPayload) =>
      connection.publishWidgetFeedbackSubmit(payload),
    endLiveSession: (reason?: string) => {
      socketStatusActions.markLiveEnding();
      return connection.endLiveSession(reason);
    },
    startNewChat: (conversationSync?: WidgetConversationSyncPayload) => connection.startNewChat(conversationSync),
    disconnect: () => {
      connection.disconnect();
      socketStatusActions.reset();
    },
    resetSession: (options?: ResetSessionOptions) => {
      connection.resetSession(options);
      socketStatusActions.reset();
    },
  };
}
